import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { flattenRegistry, FLAG_REGISTRY } from './flags.js';

// Pre-flattened at module load — FLAG_REGISTRY is a constant.
const FLAT_FLAGS = flattenRegistry(FLAG_REGISTRY);
const KNOWN_IDS = new Set(FLAT_FLAGS.map((f) => f.id));

function getDefault(id) {
  return FLAT_FLAGS.find((f) => f.id === id)?.defaultValue ?? false;
}

// Convenience hook — returns the boolean state of a single flag by id.
// Subscribes only to that flag's effective value (override or default),
// so components re-render only when that specific flag changes.
// Usage: const isOn = useFlag('wcc.mock.kpi');
export function useFlag(id) {
  return useFlagStore((s) => s.isFlagOn(id));
}

export const useFlagStore = create(
  persist(
    (set, get) => ({
      overrides: {},

      isFlagOn(id) {
        const { overrides } = get();
        return id in overrides ? overrides[id] : getDefault(id);
      },

      setFlag(id, value) {
        set((state) => {
          const overrides = { ...state.overrides };
          if (value === getDefault(id)) {
            delete overrides[id];
          } else {
            overrides[id] = value;
          }
          return { overrides };
        });
      },

      toggleFlag(id) {
        const current = get().isFlagOn(id);
        get().setFlag(id, !current);
      },

      // Bulk set: pass an array of flag IDs and a single target value.
      // Used by the admin page's "All mocks on/off" buttons (app-wide or
      // per-group). Skips unknown IDs; drops overrides that match default.
      setManyFlags(ids, value) {
        set((state) => {
          const overrides = { ...state.overrides };
          for (const id of ids) {
            if (!KNOWN_IDS.has(id)) continue;
            if (value === getDefault(id)) {
              delete overrides[id];
            } else {
              overrides[id] = value;
            }
          }
          return { overrides };
        });
      },

      resetFlags() {
        set({ overrides: {} });
      },
    }),
    {
      name: 'jobe.flags',
      // Evict overrides for flag IDs removed between deploys so the
      // "N active overrides" counter stays accurate.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const cleaned = Object.fromEntries(
          Object.entries(state.overrides).filter(([k]) => KNOWN_IDS.has(k))
        );
        if (Object.keys(cleaned).length !== Object.keys(state.overrides).length) {
          state.overrides = cleaned;
        }
      },
    }
  )
);
